import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../base-query';
import { serializeQueryArgs } from '../serialize-query-args';
import { PLATFORM_AGENT_CHATS_PAGE } from '../../services/platform-agent/platformAgentChats';

const CHANGE_SET = 'ChangeSet';

/** After apply or reject, the card shows the set shop answered (its rows' states) without loading it again. */
async function replaceCachedSet({ apiManager, id }, { dispatch, queryFulfilled }) {
  try {
    const { data } = await queryFulfilled;

    if (data) {
      dispatch(platformAgentApiSlice.util.upsertQueryData('getChangeSet', { apiManager, id }, data));
    }
  } catch {
    // The card shows the failure itself.
  }
}

/** Undo's new set (B21) goes into the cache under its own id, so its card shows it without loading it again. */
async function cacheUndoSet({ apiManager }, { dispatch, queryFulfilled }) {
  try {
    const { data } = await queryFulfilled;

    if (data?.id) {
      dispatch(platformAgentApiSlice.util.upsertQueryData('getChangeSet', { apiManager, id: data.id }, data));
    }
  } catch {
    // The card shows the failure itself.
  }
}

export const platformAgentApiSlice = createApi({
  reducerPath: 'platformAgentApiSlice',
  baseQuery,
  // The button's news: marking a chat read asks the agent again (with the panel open, on the button's next mount).
  tagTypes: [CHANGE_SET, 'PlatformAgentUnread'],
  endpoints: (builder) => ({
    // `companyId` and `franchiseId` aren't sent (the on-behalf-of headers carry them); they key the cache per
    // restaurant, so switching restaurants loads that restaurant's context.
    getPlatformAgentContext: builder.query({
      query: ({ apiManager }) => apiManager.getContext(),
      transformResponse: (response) => response?.data ?? null,
    }),
    // The button's news (contracts §6): how many answers on the restaurant the person hasn't seen. `subject` and
    // `headers` (the restaurant, the language) key the cache, so another restaurant never shows this one's count.
    getPlatformAgentUnread: builder.query({
      query: ({ apiManager, subject, headers }) => apiManager.unread({ subject }, headers),
      providesTags: ['PlatformAgentUnread'],
    }),
    getPlatformAgentHealth: builder.query({
      query: ({ apiManager }) => apiManager.get(),
    }),
    // The agent's notices in a chat (A15, GET /chats/{id} `notices`), like an import's result; `id` and `headers` (the
    // person, the restaurant, the language) key the cache.
    getPlatformAgentChatNotices: builder.query({
      query: ({ apiManager, id, headers }) => apiManager.get(id, headers),
      transformResponse: (response) => (Array.isArray(response?.notices) ? response.notices : []),
    }),
    // A change card's set (contracts §4). Apply and reject answer the set as it is now, which replaces the cached one.
    getChangeSet: builder.query({
      query: ({ apiManager, id }) => apiManager.get(id),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, { id }) => [{ type: CHANGE_SET, id }],
    }),
    // Applying an undo (a set with `undoes`) can make the set it undoes undone (B21): that card loads it again.
    applyChangeSet: builder.mutation({
      query: ({ apiManager, id, rowIds }) => apiManager.apply(id, rowIds),
      transformResponse: (response) => response?.data ?? null,
      onQueryStarted: (args, api) => replaceCachedSet(args, api),
      invalidatesTags: (result) => (result?.undoes ? [{ type: CHANGE_SET, id: result.undoes }] : []),
    }),
    rejectChangeSet: builder.mutation({
      query: ({ apiManager, id, reason }) => apiManager.reject(id, reason),
      transformResponse: (response) => response?.data ?? null,
      onQueryStarted: (args, api) => replaceCachedSet(args, api),
    }),
    // Our team's Amigo log (D8, §7): a page of requests and where it is in all of them.
    getPlatformAgentLog: builder.query({
      query: ({ apiManager, ...params }) => apiManager.getLog(params),
      transformResponse: (response) => ({ entries: response?.data ?? [], meta: response?.meta ?? null }),
    }),
    // Couldn't do (D8, §7): what Amigo couldn't do yet, grouped.
    getPlatformAgentGaps: builder.query({
      query: ({ apiManager, ...params }) => apiManager.getGaps(params),
      transformResponse: (response) => response?.data ?? [],
    }),
    // Undo on a done or partial set (B21): the new waiting set that undoes it. The set undone loads again, so its
    // `undos` lists the new one and its card shows it (D10). A failure keeps its status, so the card can show a 409's
    // own message (nothing left to undo, or an undo already waits).
    undoChangeSet: builder.mutation({
      queryFn: async ({ apiManager, id }) => {
        const response = await apiManager.undo(id);

        if (response.success) {
          return { data: response.data?.data ?? null };
        }

        return { error: { status: response.status ?? null, message: response.data?.message ?? null } };
      },
      onQueryStarted: (args, api) => cacheUndoSet(args, api),
      invalidatesTags: (result, error, { id }) => (result ? [{ type: CHANGE_SET, id }] : []),
    }),
    // Helpful? (D10) on an answer or a card, to the agent (contracts §6).
    sendPlatformAgentFeedback: builder.mutation({
      query: ({ apiManager, chatId, messageId, helpful, headers }) =>
        apiManager.feedback(chatId, { messageId, helpful }, headers),
    }),
    // The chats on a restaurant (contracts §6), from the agent: 30 a page, newest first; `next_cursor` is the next
    // page's, null on the last. `subject` and `headers` (the person, the restaurant, the language) key the cache.
    getPlatformAgentChats: builder.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage?.next_cursor ?? null,
      },
      query: ({ queryArg: { apiManager, subject, headers }, pageParam }) =>
        apiManager.list({ subject, cursor: pageParam, limit: PLATFORM_AGENT_CHATS_PAGE }, headers),
    }),
    // New chat: an eve session on the restaurant, which the panel then shows empty.
    createPlatformAgentChat: builder.mutation({
      query: ({ apiManager, headers }) => apiManager.create(headers),
      transformResponse: (response) => response?.chat ?? null,
    }),
    // The person has seen a chat's answers: reopened from the list, the chat the panel opened on with news, or an
    // answer that came in while the chat showed (usePlatformAgentChats). The button's count then comes from the agent.
    markPlatformAgentChatRead: builder.mutation({
      query: ({ apiManager, id, headers }) => apiManager.markRead(id, headers),
      invalidatesTags: ['PlatformAgentUnread'],
    }),
  }),
  serializeQueryArgs,
});

export const {
  useGetPlatformAgentContextQuery,
  useGetPlatformAgentUnreadQuery,
  useGetPlatformAgentHealthQuery,
  useGetPlatformAgentChatNoticesQuery,
  useGetChangeSetQuery,
  useApplyChangeSetMutation,
  useRejectChangeSetMutation,
  useGetPlatformAgentLogQuery,
  useGetPlatformAgentGapsQuery,
  useUndoChangeSetMutation,
  useSendPlatformAgentFeedbackMutation,
  useGetPlatformAgentChatsInfiniteQuery,
  useCreatePlatformAgentChatMutation,
  useMarkPlatformAgentChatReadMutation,
} = platformAgentApiSlice;
